export enum MROIChartType {
  COLUMN = "Column",
  BUBBLE = "Bubble",
}

export enum MROIColumnSelector {
  ROI_PERCENTAGE = "ROI_PERCENTAGE",
  SPEND_VS_REVENUE_AMOUNT = "SPEND_VS_REVENUE_AMOUNT",
  SPEND_VS_REVENUE_PERCENTAGE = "SPEND_VS_REVENUE_PERCENTAGE",
  SHARE_OF_EFFECT_AMOUNT = "SHARE_OF_EFFECT_AMOUNT",
  SHARE_OF_EFFECT_PERCENTAGE = "SHARE_OF_EFFECT_PERCENTAGE",
  MARKETING_RETURN_AMOUNT = "MARKETING_RETURN_AMOUNT",
  MARKETING_RETURN_PERCENTAGE = "MARKETING_RETURN_PERCENTAGE",
}

export enum SharedRevenueColumnSelector {
  ROI_PERCENTAGE = "ROI_PERCENTAGE",
  SPEND_VS_REVENUE_AMOUNT = "SPEND_VS_REVENUE_AMOUNT",
  SPEND_VS_REVENUE_PERCENTAGE = "SPEND_VS_REVENUE_PERCENTAGE",
}

export interface MROIGridFilterMetadata {
  key: string;
  label: string;
  type: "percentage" | "currency" | "number";
  category: "total" | "upper_funnel" | "mid_funnel" | "lower_funnel" | "ad_stock";
  metric: "spend" | "revenue" | "roi" | "contribution" | "ad_stock";
}

export enum MROIGridFilters {
  TOTAL_SPEND_AMOUNT = "TOTAL_SPEND_AMOUNT",
  TOTAL_REVENUE_AMOUNT = "TOTAL_REVENUE_AMOUNT",
  TOTAL_ROI_PERCENTAGE = "TOTAL_ROI_PERCENTAGE",
  TOTAL_SPEND_PERCENTAGE = "TOTAL_SPEND_PERCENTAGE",
  TOTAL_REVENUE_PERCENTAGE = "TOTAL_REVENUE_PERCENTAGE",
  TOTAL_YIELD_AMOUNT = "TOTAL_YIELD_AMOUNT",
  CONTRIBUTION_PERCENTAGE = "CONTRIBUTION_PERCENTAGE",
  UPPER_FUNNEL_SPEND_PERCENTAGE = "UPPER_FUNNEL_SPEND_PERCENTAGE",
  MID_FUNNEL_SPEND_PERCENTAGE = "MID_FUNNEL_SPEND_PERCENTAGE",
  LOWER_FUNNEL_SPEND_PERCENTAGE = "LOWER_FUNNEL_SPEND_PERCENTAGE",
  UPPER_FUNNEL_REVENUE_PERCENTAGE = "UPPER_FUNNEL_REVENUE_PERCENTAGE",
  MID_FUNNEL_REVENUE_PERCENTAGE = "MID_FUNNEL_REVENUE_PERCENTAGE",
  LOWER_FUNNEL_REVENUE_PERCENTAGE = "LOWER_FUNNEL_REVENUE_PERCENTAGE",
  UPPER_FUNNEL_ROI_PERCENTAGE = "UPPER_FUNNEL_ROI_PERCENTAGE",
  MID_FUNNEL_ROI_PERCENTAGE = "MID_FUNNEL_ROI_PERCENTAGE",
  LOWER_FUNNEL_ROI_PERCENTAGE = "LOWER_FUNNEL_ROI_PERCENTAGE",
  UPPER_FUNNEL_SPEND_AMOUNT = "UPPER_FUNNEL_SPEND_AMOUNT",
  MID_FUNNEL_SPEND_AMOUNT = "MID_FUNNEL_SPEND_AMOUNT",
  LOWER_FUNNEL_SPEND_AMOUNT = "LOWER_FUNNEL_SPEND_AMOUNT",
  UPPER_FUNNEL_REVENUE_AMOUNT = "UPPER_FUNNEL_REVENUE_AMOUNT",
  MID_FUNNEL_REVENUE_AMOUNT = "MID_FUNNEL_REVENUE_AMOUNT",
  LOWER_FUNNEL_REVENUE_AMOUNT = "LOWER_FUNNEL_REVENUE_AMOUNT",
  UPPER_FUNNEL_YIELD_AMOUNT = "UPPER_FUNNEL_YIELD_AMOUNT",
  MID_FUNNEL_YIELD_AMOUNT = "MID_FUNNEL_YIELD_AMOUNT",
  LOWER_FUNNEL_YIELD_AMOUNT = "LOWER_FUNNEL_YIELD_AMOUNT",
  AD_STOCK_PEAK = "AD_STOCK_PEAK",
  AD_STOCK_LENGTH = "AD_STOCK_LENGTH",
  UPPER_FUNNEL_MARKETING_CONTRIBUTION_PERCENTAGE = "UPPER_FUNNEL_MARKETING_CONTRIBUTION_PERCENTAGE",
  MID_FUNNEL_MARKETING_CONTRIBUTION_PERCENTAGE = "MID_FUNNEL_MARKETING_CONTRIBUTION_PERCENTAGE",
  LOWER_FUNNEL_MARKETING_RETURN_PERCENTAGE = "LOWER_FUNNEL_MARKETING_RETURN_PERCENTAGE",
  TOTAL_MARKETING_REVENUE_AMOUNT = "TOTAL_MARKETING_REVENUE_AMOUNT",
  TOTAL_MARKETING_ROI_PERCENTAGE = "TOTAL_MARKETING_ROI_PERCENTAGE",
  UPPER_FUNNEL_MARKETING_CONTRIBUTION_AMOUNT = "UPPER_FUNNEL_MARKETING_CONTRIBUTION_AMOUNT",
  MID_FUNNEL_MARKETING_CONTRIBUTION_AMOUNT = "MID_FUNNEL_MARKETING_CONTRIBUTION_AMOUNT",
}

export const MROI_GRID_FILTERS: Record<string, MROIGridFilterMetadata> = {
  [MROIGridFilters.TOTAL_SPEND_AMOUNT]: {
    key: MROIGridFilters.TOTAL_SPEND_AMOUNT,
    label: "Total Spend",
    type: "currency",
    category: "total",
    metric: "spend",
  },
  [MROIGridFilters.TOTAL_REVENUE_AMOUNT]: {
    key: MROIGridFilters.TOTAL_REVENUE_AMOUNT,
    label: "Total Revenue",
    type: "currency",
    category: "total",
    metric: "revenue",
  },
  [MROIGridFilters.TOTAL_SPEND_PERCENTAGE]: {
    key: MROIGridFilters.TOTAL_SPEND_PERCENTAGE,
    label: "Total Spend",
    type: "percentage",
    category: "total",
    metric: "spend",
  },
  [MROIGridFilters.TOTAL_REVENUE_PERCENTAGE]: {
    key: MROIGridFilters.TOTAL_REVENUE_PERCENTAGE,
    label: "Total Revenue",
    type: "percentage",
    category: "total",
    metric: "revenue",
  },
  [MROIGridFilters.TOTAL_ROI_PERCENTAGE]: {
    key: MROIGridFilters.TOTAL_ROI_PERCENTAGE,
    label: "Total ROI",
    type: "percentage",
    category: "total",
    metric: "roi",
  },
  [MROIGridFilters.TOTAL_YIELD_AMOUNT]: {
    key: MROIGridFilters.TOTAL_YIELD_AMOUNT,
    label: "Total Yield",
    type: "currency",
    category: "total",
    metric: "roi",
  },
  [MROIGridFilters.CONTRIBUTION_PERCENTAGE]: {
    key: MROIGridFilters.CONTRIBUTION_PERCENTAGE,
    label: "Contribution",
    type: "percentage",
    category: "total",
    metric: "contribution",
  },
  [MROIGridFilters.UPPER_FUNNEL_SPEND_PERCENTAGE]: {
    key: MROIGridFilters.UPPER_FUNNEL_SPEND_PERCENTAGE,
    label: "Upper Funnel Spend",
    type: "percentage",
    category: "upper_funnel",
    metric: "spend",
  },
  [MROIGridFilters.MID_FUNNEL_SPEND_PERCENTAGE]: {
    key: MROIGridFilters.MID_FUNNEL_SPEND_PERCENTAGE,
    label: "Mid Funnel Spend",
    type: "percentage",
    category: "mid_funnel",
    metric: "spend",
  },
  [MROIGridFilters.LOWER_FUNNEL_SPEND_PERCENTAGE]: {
    key: MROIGridFilters.LOWER_FUNNEL_SPEND_PERCENTAGE,
    label: "Lower Funnel Spend",
    type: "percentage",
    category: "lower_funnel",
    metric: "spend",
  },
  [MROIGridFilters.UPPER_FUNNEL_REVENUE_PERCENTAGE]: {
    key: MROIGridFilters.UPPER_FUNNEL_REVENUE_PERCENTAGE,
    label: "Upper Funnel Revenue",
    type: "percentage",
    category: "upper_funnel",
    metric: "revenue",
  },
  [MROIGridFilters.MID_FUNNEL_REVENUE_PERCENTAGE]: {
    key: MROIGridFilters.MID_FUNNEL_REVENUE_PERCENTAGE,
    label: "Mid Funnel Revenue",
    type: "percentage",
    category: "mid_funnel",
    metric: "revenue",
  },
  [MROIGridFilters.LOWER_FUNNEL_REVENUE_PERCENTAGE]: {
    key: MROIGridFilters.LOWER_FUNNEL_REVENUE_PERCENTAGE,
    label: "Lower Funnel Revenue",
    type: "percentage",
    category: "lower_funnel",
    metric: "revenue",
  },
  [MROIGridFilters.UPPER_FUNNEL_ROI_PERCENTAGE]: {
    key: MROIGridFilters.UPPER_FUNNEL_ROI_PERCENTAGE,
    label: "Upper Funnel ROI",
    type: "percentage",
    category: "upper_funnel",
    metric: "roi",
  },
  [MROIGridFilters.MID_FUNNEL_ROI_PERCENTAGE]: {
    key: MROIGridFilters.MID_FUNNEL_ROI_PERCENTAGE,
    label: "Mid Funnel ROI",
    type: "percentage",
    category: "mid_funnel",
    metric: "roi",
  },
  [MROIGridFilters.LOWER_FUNNEL_ROI_PERCENTAGE]: {
    key: MROIGridFilters.LOWER_FUNNEL_ROI_PERCENTAGE,
    label: "Lower Funnel ROI",
    type: "percentage",
    category: "lower_funnel",
    metric: "roi",
  },
  [MROIGridFilters.UPPER_FUNNEL_SPEND_AMOUNT]: {
    key: MROIGridFilters.UPPER_FUNNEL_SPEND_AMOUNT,
    label: "Upper Funnel Spend",
    type: "currency",
    category: "upper_funnel",
    metric: "spend",
  },
  [MROIGridFilters.MID_FUNNEL_SPEND_AMOUNT]: {
    key: MROIGridFilters.MID_FUNNEL_SPEND_AMOUNT,
    label: "Mid Funnel Spend",
    type: "currency",
    category: "mid_funnel",
    metric: "spend",
  },
  [MROIGridFilters.LOWER_FUNNEL_SPEND_AMOUNT]: {
    key: MROIGridFilters.LOWER_FUNNEL_SPEND_AMOUNT,
    label: "Lower Funnel Spend",
    type: "currency",
    category: "lower_funnel",
    metric: "spend",
  },
  [MROIGridFilters.UPPER_FUNNEL_REVENUE_AMOUNT]: {
    key: MROIGridFilters.UPPER_FUNNEL_REVENUE_AMOUNT,
    label: "Upper Funnel Revenue",
    type: "currency",
    category: "upper_funnel",
    metric: "revenue",
  },
  [MROIGridFilters.MID_FUNNEL_REVENUE_AMOUNT]: {
    key: MROIGridFilters.MID_FUNNEL_REVENUE_AMOUNT,
    label: "Mid Funnel Revenue",
    type: "currency",
    category: "mid_funnel",
    metric: "revenue",
  },
  [MROIGridFilters.LOWER_FUNNEL_REVENUE_AMOUNT]: {
    key: MROIGridFilters.LOWER_FUNNEL_REVENUE_AMOUNT,
    label: "Lower Funnel Revenue",
    type: "currency",
    category: "lower_funnel",
    metric: "revenue",
  },
  [MROIGridFilters.UPPER_FUNNEL_YIELD_AMOUNT]: {
    key: MROIGridFilters.UPPER_FUNNEL_YIELD_AMOUNT,
    label: "Upper Funnel Yield",
    type: "currency",
    category: "upper_funnel",
    metric: "roi",
  },
  [MROIGridFilters.MID_FUNNEL_YIELD_AMOUNT]: {
    key: MROIGridFilters.MID_FUNNEL_YIELD_AMOUNT,
    label: "Mid Funnel Yield",
    type: "currency",
    category: "mid_funnel",
    metric: "roi",
  },
  [MROIGridFilters.LOWER_FUNNEL_YIELD_AMOUNT]: {
    key: MROIGridFilters.LOWER_FUNNEL_YIELD_AMOUNT,
    label: "Lower Funnel Yield",
    type: "currency",
    category: "lower_funnel",
    metric: "roi",
  },
  [MROIGridFilters.AD_STOCK_PEAK]: {
    key: MROIGridFilters.AD_STOCK_PEAK,
    label: "Ad Stock Peak",
    type: "number",
    category: "ad_stock",
    metric: "ad_stock",
  },
  [MROIGridFilters.AD_STOCK_LENGTH]: {
    key: MROIGridFilters.AD_STOCK_LENGTH,
    label: "Ad Stock Length",
    type: "number",
    category: "ad_stock",
    metric: "ad_stock",
  },
  [MROIGridFilters.UPPER_FUNNEL_MARKETING_CONTRIBUTION_PERCENTAGE]: {
    key: MROIGridFilters.UPPER_FUNNEL_MARKETING_CONTRIBUTION_PERCENTAGE,
    label: "Upper Funnel Marketing Contribution",
    type: "percentage",
    category: "upper_funnel",
    metric: "contribution",
  },
  [MROIGridFilters.MID_FUNNEL_MARKETING_CONTRIBUTION_PERCENTAGE]: {
    key: MROIGridFilters.MID_FUNNEL_MARKETING_CONTRIBUTION_PERCENTAGE,
    label: "Mid Funnel Marketing Contribution",
    type: "percentage",
    category: "mid_funnel",
    metric: "contribution",
  },
  [MROIGridFilters.LOWER_FUNNEL_MARKETING_RETURN_PERCENTAGE]: {
    key: MROIGridFilters.LOWER_FUNNEL_MARKETING_RETURN_PERCENTAGE,
    label: "Lower Funnel Marketing Return",
    type: "percentage",
    category: "lower_funnel",
    metric: "contribution",
  },
  [MROIGridFilters.TOTAL_MARKETING_REVENUE_AMOUNT]: {
    key: MROIGridFilters.TOTAL_MARKETING_REVENUE_AMOUNT,
    label: "Total Marketing Revenue",
    type: "currency",
    category: "total",
    metric: "revenue",
  },
  [MROIGridFilters.TOTAL_MARKETING_ROI_PERCENTAGE]: {
    key: MROIGridFilters.TOTAL_MARKETING_ROI_PERCENTAGE,
    label: "Total Marketing ROI",
    type: "percentage",
    category: "total",
    metric: "roi",
  },
  [MROIGridFilters.UPPER_FUNNEL_MARKETING_CONTRIBUTION_AMOUNT]: {
    key: MROIGridFilters.UPPER_FUNNEL_MARKETING_CONTRIBUTION_AMOUNT,
    label: "Upper Funnel Marketing Contribution",
    type: "currency",
    category: "upper_funnel",
    metric: "contribution",
  },
  [MROIGridFilters.MID_FUNNEL_MARKETING_CONTRIBUTION_AMOUNT]: {
    key: MROIGridFilters.MID_FUNNEL_MARKETING_CONTRIBUTION_AMOUNT,
    label: "Mid Funnel Marketing Contribution",
    type: "currency",
    category: "mid_funnel",
    metric: "contribution",
  },
} as const;

export type MROIGridFilter = keyof typeof MROI_GRID_FILTERS;
